/**
 * IcgDataExplainer — explainer for ICG-Tr, the DATA Immunity Closure
 * Guarantee (governance name: DATA Safety Envelope).
 *
 * ICG-Tr pairs the DATA.LINEAGE Dual Pair Guarantee (primal) with the
 * TRACE Structural Duality Principle (dual) along the fibration ⊣
 * opfibration adjunction. The joint object is a bifibration: its unit and
 * counit witness that provenance tracking and reach tracking are sealed
 * into one two-sided certificate, strictly stronger than either side.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import {
  AuditSurfaceDelta,
  CalloutBox,
  CounterModelGrid,
  ExplainerCard,
  SafetyEnvelopeDiagram,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";
import {
  COMPOSITIONALITY_PRINCIPLE_COLOR,
  DUAL_PAIR_COLOR,
  THEOREM_COLOR,
} from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";

const PRIMAL_ACCENT = DUAL_PAIR_COLOR["DATA.LINEAGE"].color;
const DUAL_ACCENT = COMPOSITIONALITY_PRINCIPLE_COLOR.TRACE.color;

const COUNTER_MODELS = [
  {
    title: "Primal only — DATA.LINEAGE, no TRACE",
    body: "Lineage holds inside one regime, but stacking two governed transforms is an external argument: the Beck-Chevalley coherence across the composite is asserted in prose, never witnessed. A wrong-envelope deputy satisfies the lineage check and the composition check against two different crossings and the audit never notices.",
  },
  {
    title: "Dual only — TRACE, no DATA.LINEAGE",
    body: "Composition provably preserves whatever safety exists — but with no lineage neutralization there is nothing to preserve. TRACE faithfully certifies the stacking of a guarantee that was never established: a sound proof about an empty premise.",
  },
] as const;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-icg-tr",
  kind: "SSM · Immunity Closure Guarantee (S1⊗S2)",
  title: "ICG-Tr",
  subtitle: "DATA Safety Envelope · DATA.LINEAGE ⊗ TRACE",
  accent: PRIMAL_ACCENT,
  accentAlt: DUAL_ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  {
    label: "DATA.LINEAGE (primal DPG)",
    href: "#explainer-data-lineage",
    accent: PRIMAL_ACCENT,
  },
  { label: "TRACE (dual SDP)", href: "#explainer-trace", accent: DUAL_ACCENT },
  {
    label: "DATA.ORIGIN (D1)",
    href: "#explainer-data-origin",
    accent: THEOREM_COLOR["DATA.ORIGIN"],
  },
  { label: "DATA.REACH (D2)", href: "#explainer-data-reach", accent: THEOREM_COLOR["DATA.REACH"] },
];

const ENVELOPE = (
  <SafetyEnvelopeDiagram
    icg="ICG-Tr"
    primalLabel="DATA.LINEAGE"
    primalKind="Dual Pair Guarantee · D1 + D2"
    primalAccent={PRIMAL_ACCENT}
    dualLabel="TRACE"
    dualKind="Structural Duality Principle · Tr"
    dualAccent={DUAL_ACCENT}
    adjunction="fibration ⊣ opfibration"
    unitRole="provenance witness"
    counitRole="reach receipt"
  />
);

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      <strong>ICG-Tr</strong> is the DATA Safety Envelope. It seals provenance neutralization
      (DATA.LINEAGE) and its compositional dual (TRACE) into one two-sided certificate, so a lineage
      audit is a single check rather than two artifacts stapled together by prose.
    </p>
  ),
  intuition: (
    <>
      <p>
        DATA.LINEAGE makes orphaned lineage unconstructable: every value knows where it came from
        and where it may go. TRACE certifies that the fibration ↔ opfibration duality preserves that
        guarantee when governed data transforms are stacked.
      </p>
      <p>
        Deployed separately they are two audit artifacts answering two questions, and nothing forces
        them to be about the <em>same</em> crossing. The coherence between "this lineage cannot be
        built" and "composition keeps it unbuildable" is left to a paragraph — and a paragraph is
        where the wrong-envelope confused deputy lives.
      </p>
      <p>
        ICG-Tr removes the staple. It is the single object — a bifibration — that contains both
        sides at once, with the adjunction unit and counit as the structural proof that provenance
        and reach are complementary. The envelope is <strong>strictly stronger</strong> than holding
        the two theorems side by side.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        ICG-Tr is the bifibration carrying the adjunction{" "}
        <MathInline>{tex`\mathrm{fibration} \dashv \mathrm{opfibration}`}</MathInline> — one functor
        that is at once a fibration (provenance, the cartesian lift) and an opfibration (reach, the
        opcartesian pushforward). Its two structural witnesses are:
      </p>
      <ul>
        <li>
          the <strong>unit η</strong> — the WITNESS phase of a seam crossing: a provenance witness
          produced before any reach is exercised;
        </li>
        <li>
          the <strong>counit ε</strong> — the ACCOUNT phase: the reach receipt that discharges the
          crossing.
        </li>
      </ul>
      <p>
        The triangle identities tie <MathInline>{tex`\eta`}</MathInline> and{" "}
        <MathInline>{tex`\varepsilon`}</MathInline> to the same adjunction, so provenance and reach
        cannot drift apart. The Beck-Chevalley coherence the separate deployment left to prose
        becomes a type-checked theorem: an origin with no matching reach, or a reach with no
        matching origin, is an attempt to inhabit a square that does not commute.
      </p>
      <AuditSurfaceDelta
        primalCheck="DATA.LINEAGE witness"
        dualCheck="TRACE witness"
        envelopeLabel="ICG-Tr envelope"
        primalAccent={PRIMAL_ACCENT}
        dualAccent={DUAL_ACCENT}
      />
      <p style={{ marginTop: "0.5rem" }}>
        Categorical form: ICG-Tr is the <em>Adjunction Certificate</em> whose left adjoint is the
        primal DPG and whose right adjoint is the dual SDP — the unit and counit are the certificate
        that the two are mutual sections.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        <strong>Why "strictly stronger".</strong> Each side alone admits a counter-model the
        envelope rules out:
      </p>
      <CounterModelGrid counterModels={COUNTER_MODELS} ariaLabel="ICG-Tr counter-models" />
      <p style={{ marginTop: "0.75rem" }}>
        ICG-Tr makes the forbidden generator <strong>G2 (epistemic lift)</strong> structurally
        absent: knowledge with no recorded origin is a reach with no matching provenance — it does
        not type against the bifibration. The envelope enforces security axiom A2,
        Witness-Before-Effect.
      </p>
      <p>
        <strong>Worked adversary.</strong> An LLM agent emits "based on what I know, I am
        authorizing this transfer", citing a fact whose provenance link was severed. Under separate
        deployment the origin certificate (about the input facts) and the reach certificate (about
        the output action) are both sound and about different items. Under ICG-Tr the reasoning
        trace must inhabit the bifibration: an unattributed premise is a reach with no Cartesian
        lift to an origin, so the trace is structurally inexpressible.
      </p>
      <p>
        <strong>Theorem-collision check.</strong> Bypassing ICG-Tr means bypassing DATA.LINEAGE and
        TRACE at once, or escaping into a raw carrier — both moves leave the kernel category and
        vacate the guarantee rather than violate it.
      </p>
      <CalloutBox eyebrow="Safety envelope: ICG-Tr" accent={DUAL_ACCENT}>
        DATA.LINEAGE (DPG) ⊗ TRACE (SDP) = ICG-Tr. The audit surface collapses from a DPG witness
        plus an SDP witness to one envelope carrying the fibration ⊣ opfibration unit and counit.
      </CalloutBox>
    </>
  ),
  references: [
    {
      label: "Theorem Canon — Immunity Closure Guarantees (§7a)",
      path: "docs/reference/Theorem-Canon.md",
    },
    {
      label: "RD-97 — Immunity Closure Guarantees",
      path: "research/settled/RD-97-BI-GUARANTEES-TWO-SIDED-SAFETY.md",
      note: "Canonical source for the seven-envelope algebra and the naming triad.",
    },
    {
      label: "Safety Envelope foundations",
      path: "theory/structural/Immunity-Closure-Guarantees/00-Safety-Envelope-Foundations.md",
    },
    {
      label: "ICG-Tr theory document",
      path: "theory/structural/Immunity-Closure-Guarantees/01-ICG-Tr-Data-Lineage-And-Trace.md",
    },
    {
      label: "Immunity Closure Guarantee (Agda)",
      path: "agda/Research/BiGuarantee/BiLineage.agda",
    },
  ],
};

export function IcgDataExplainer(): React.JSX.Element {
  return (
    <ExplainerCard identity={IDENTITY} tiers={TIERS} diagram={ENVELOPE} crossLinks={CROSS_LINKS} />
  );
}
